import { useState } from "react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { useCurrentCompany, useCurrentUser, useSession } from "../../services/SessionContext";
import type { CompanyRole, MemberStatus } from "../../types/domain";

const roles: CompanyRole[] = ["OWNER", "ADMIN", "TECHNICIAN", "VIEWER"];

export function TeamPage() {
  const { repo, inviteMember, updateMember } = useSession();
  const company = useCurrentCompany();
  const user = useCurrentUser();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CompanyRole>("TECHNICIAN");
  const [error, setError] = useState("");
  if (!company || !user) return null;
  const currentCompany = company;
  const activeMember = repo.activeMember(currentCompany.companyId, user.userId);
  const canManage = activeMember && ["OWNER", "ADMIN"].includes(activeMember.role);
  const members = repo.getMemberUsers(currentCompany.companyId);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    setError("");
    try {
      inviteMember(currentCompany.companyId, email, role);
      setEmail("");
      setRole("TECHNICIAN");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invite failed.");
    }
  }

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div>
        <h1 className="text-2xl font-bold text-navy-950">Company team</h1>
        <p className="mt-1 text-sm text-slate-600">Manage workspace access for {currentCompany.name}.</p>
      </div>

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Invite user</CardTitle>
          </CardHeader>
          <CardContent>
            <form className="grid gap-4 sm:grid-cols-[1fr_180px_auto]" onSubmit={submit}>
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input id="invite-email" inputMode="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select id="invite-role" value={role} onChange={(event) => setRole(event.target.value as CompanyRole)}>
                  {roles.filter((item) => item !== "OWNER").map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="self-end">
                <Button type="submit">Invite teammate</Button>
              </div>
            </form>
            {error ? <p className="mt-3 text-sm font-medium text-red-700">{error}</p> : null}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {members.map(({ member, user: memberUser }) => (
            <div key={member.userId} className="grid gap-3 rounded-lg border border-slate-200 p-4 sm:grid-cols-[1fr_170px_170px] sm:items-center">
              <div>
                <p className="font-semibold text-navy-900">{memberUser?.name || memberUser?.email || member.invitedEmail}</p>
                <p className="text-sm text-slate-600">{memberUser?.email || member.invitedEmail}</p>
              </div>
              {canManage && member.role !== "OWNER" ? (
                <Select value={member.role} onChange={(event) => updateMember(currentCompany.companyId, member.userId, { role: event.target.value as CompanyRole })}>
                  {roles.filter((item) => item !== "OWNER").map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </Select>
              ) : (
                <Badge className="w-fit bg-slate-50 text-slate-700 ring-slate-200">{member.role}</Badge>
              )}
              {canManage && member.role !== "OWNER" ? (
                <Select value={member.status} onChange={(event) => updateMember(currentCompany.companyId, member.userId, { status: event.target.value as MemberStatus })}>
                  {["ACTIVE", "INVITED", "DISABLED"].map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              ) : (
                <Badge className="w-fit bg-field-100 text-field-600 ring-field-600/20">{member.status}</Badge>
              )}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
